import { Button, Flex, Input, Typography } from "antd";
import React, { useEffect, useState } from "react";

/**
 * Body of the patient-note popover rendered inline against the tag Select.
 * Used for two flows:
 *
 *   mode="add"     — a new tag was just selected; capture/edit the
 *                    shared patient note. Empty body clears the note.
 *   mode="remove"  — a tag is about to be removed and the patient already
 *                    has a note; let staff edit the note in the same gesture.
 *                    Empty body clears the note.
 *
 * The popover positioning is owned by the caller (an AntD `Popover`
 * anchored to the tag Select). This component is the popover's content.
 */
export default function TagNotePopover({ mode, initialNote, onCancel, onConfirm }) {
  const [note, setNote] = useState(initialNote || "");

  // Re-seed the textarea when the caller hands us a different patient or
  // re-opens the popover. We watch `initialNote` because that's the only
  // way the caller signals "this is a fresh open, not a re-render".
  useEffect(() => {
    setNote(initialNote || "");
  }, [initialNote]);

  const isRemove = mode === "remove";
  const title = isRemove ? "Removing tag — review note" : "Add or update note";
  const confirmLabel = isRemove ? "Remove tag" : "Save";
  const placeholder = isRemove
    ? "Update the note before removing, or leave as-is."
    : "Add a note (optional) — what should the next person know?";

  return (
    <Flex vertical gap={10} style={{ width: 320 }} data-onboarding-id="tag-note-popover">
      <Typography.Text strong>{title}</Typography.Text>
      <Input.TextArea
        rows={4}
        placeholder={placeholder}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        autoFocus
      />
      <Flex gap={8} style={{ marginLeft: "auto" }}>
        <Button
          size="small"
          style={{ border: "1px solid #6b43b5", color: "#6b43b5" }}
          data-onboarding-id="tag-note-cancel"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          size="small"
          type="primary"
          danger={isRemove}
          data-onboarding-id="tag-note-save"
          onClick={() => onConfirm(note.trim())}
        >
          {confirmLabel}
        </Button>
      </Flex>
    </Flex>
  );
}
